import { IPromise, IQService, IScope, ITimeoutService } from "angular";
import { StateParams, StateService } from "@uirouter/angularjs";
import { AccountType, IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { getCurrentUserId } from "@gtmhub/users";
import { CurrentUserService } from "@gtmhub/users/current-user.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Task, TaskParentType, TaskSort, TasksViewMode } from "@webapp/tasks/models/tasks.models";
import { IHubFilter } from "../../hub/models/hub";
import { HubService } from "../../hub/services/hub";
import { TaskFilterModalService } from "../services/task-filter-modal.service";
import { TaskService } from "../services/task.service";
import { ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents } from "../task.events";

interface ITasksRenderingStep {
  step: number;
}
interface ITasksRenderingLimit {
  limitTo: number;
}
interface ITasksListRenderingOptions extends ITasksRenderingStep, ITasksRenderingLimit {}
interface ITaskKanbanRenderingOptions extends ITasksRenderingStep {
  todo: ITasksRenderingLimit;
  inprogress: ITasksRenderingLimit;
  done: ITasksRenderingLimit;
}
type GtmhubTaskStatus = "todo" | "inprogress" | "done";

export interface ITasksScope extends IScope {
  tasksColumns: {
    done: Task[];
    inprogress: Task[];
    todo: Task[];
  };
  dueDate: string;
  taskList: Task[];
  userHasCustomViews: boolean;
  // Used in order to render empty-state only in case there are neither archived tasks nor active ones
  archivedTasksCount: number;
  mode: TasksViewMode;
  indicators: {
    loadingTasks?: IIndicator;
  };
  isCreatingNewKanbanTask: boolean;
  isSortedBy: {
    name: boolean;
    dueDate: boolean;
    status: boolean;
    parentName: boolean;
  };
  isAscSort: boolean;
  renderingOptions: {
    list: ITasksListRenderingOptions;
    kanban: ITaskKanbanRenderingOptions;
  };
  isDemoAccount: boolean;
  isBannerEnabled: boolean;
  toggleTaskCellHover(isHovered: boolean): void;
  toggleMode(mode: TasksViewMode): void;
  dropped(event, index, type, external, column: string): void;
  deleteTask(task: Task): void;
  archive(task: Task): void;
  clone(task: Task): void;
  createNewTaskInList(): void;
  openTaskFormInKanban(): void;
  setDueDate(task: Task, date: string): void;
  sortBy(sort: string, changeAscDesSort?: { changeAscDesSort: boolean }): void;
  increaseListLimit(): void;
  increaseKanbanLimit(columnName: GtmhubTaskStatus): void;
  openFilterModal(): void;
  closeFormTaskInKanban(): void;
}

const sizes = {
  taskRowHeight: 39,
  taskKanbanHeight: 130,
};

export class TasksCtrl implements IStateInit {
  private taskEvents: TaskEvents;
  private sortTask: TaskSort;
  private tasksOwner: string | string[];

  public static $inject = [
    "$scope",
    "$state",
    "$q",
    "CurrentUserService",
    "UIErrorHandlingService",
    "GoalService",
    "$stateParams",
    "HubService",
    "TaskService",
    "$timeout",
    "MetricService",
    "AccountResolverService",
    "ApmSpanService",
    "TaskFilterModalService",
    "Ng1ConfirmDeleteService",
  ];

  constructor(
    private $scope: ITasksScope,
    private $state: StateService,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalService: GoalService,
    private $stateParams: StateParams,
    private hubService: HubService,
    private tasksService: TaskService,
    private $timeout: ITimeoutService,
    private metricService: MetricService,
    private accountResolverService: AccountResolverService,
    private apmSpanService: ApmSpanService,
    private taskFilterModalService: TaskFilterModalService,
    private confirmDeleteService: Ng1ConfirmDeleteService
  ) {
    this.taskEvents = new TaskEvents(this.$scope);

    this.$scope.indicators = {};

    this.$scope.isSortedBy = {
      name: false,
      dueDate: false,
      status: false,
      parentName: false,
    };

    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };
    this.$scope.archivedTasksCount = 0;
    this.$scope.isDemoAccount = this.accountResolverService.getAccountData().type === AccountType.DemoAccount;

    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));
    this.$scope.$on("filterChanged", this.init);

    this.$scope.closeFormTaskInKanban = this.closeFormTaskInKanban;
    this.$scope.createNewTaskInList = this.createNewTaskInList;
    this.$scope.openTaskFormInKanban = this.openTaskFormInKanban;
    this.$scope.dropped = this.dropped;
    this.$scope.deleteTask = this.deleteTask;
    this.$scope.openFilterModal = this.openFilterModal;
    this.$scope.toggleMode = this.toggleMode;
    this.$scope.archive = this.archive;
    this.$scope.clone = this.clone;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.sortBy = this.sortBy;
    this.$scope.increaseListLimit = this.increaseListLimit;
    this.$scope.increaseKanbanLimit = this.increaseKanbanLimit;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private toggleMode = (mode: TasksViewMode): void => {
    if (mode === this.$scope.mode) {
      return;
    }

    this.$scope.mode = mode;
    this.setInitialRenderingLimits();
    this.currentUserService.setUserSetting({ taskView: this.$scope.mode });
  };

  private init = (): IPromise<unknown> => {
    return this.apmSpanService.dataLoadSpan("init", () => {
      this.$scope.indicators.loadingTasks = { progress: true };

      this.setInitialView();
      this.setInitialSort();

      const customViewsPromise = this.hubService.getFilters("hub");

      let loadFilterPromise: IPromise<IHubFilter>;

      if (this.$stateParams.filterId) {
        loadFilterPromise = this.apmSpanService.dataLoadSpan("filter", () => this.hubService.getFilter(this.$stateParams.filterId));
      }

      this.$scope.isBannerEnabled = this.accountResolverService.getAccountData().type === AccountType.TrialAccount;

      return this.$q
        .all({
          loadFilter: loadFilterPromise,
          customViews: customViewsPromise,
        })
        .then(
          ({ loadFilter, customViews }) => {
            return this.apmSpanService.dataLoadSpan("tasks", () => {
              this.$scope.userHasCustomViews = customViews.length > 0;
              this.tasksOwner = loadFilter ? this.processFilterIds(loadFilter) : getCurrentUserId();

              return this.loadTasks(this.tasksOwner);
            });
          },
          (err) => {
            this.uiErrorHandlingService.handleModal(err);
          }
        );
    });
  };

  private sortBy = (sort: TaskSort, { changeAscDesSort } = { changeAscDesSort: true }): void => {
    if (this.sortTask?.includes(sort) && changeAscDesSort) {
      this.setAscDesSort(sort);
    } else {
      this.sortTask = sort;
    }

    Object.keys(this.$scope.isSortedBy).forEach((key) => {
      if (key === this.sortTask.replace("-", "")) {
        this.$scope.isSortedBy[key] = true;
      } else {
        this.$scope.isSortedBy[key] = false;
      }
    });

    this.$state.go(".", { tasksSortBy: this.sortTask }, { notify: false });
    this.currentUserService.setUserSetting({ taskOrder: this.sortTask });
    this.init();
  };

  private setAscDesSort = (sort: TaskSort): void => {
    if (this.sortTask.includes("-")) {
      this.sortTask = this.sortTask.replace("-", "") as TaskSort;
    } else {
      this.sortTask = `-${sort}` as TaskSort;
    }

    this.$scope.isAscSort = this.sortTask.includes("-");
  };

  private setInitialView = () => {
    if (this.currentUserService.getUserSetting("taskView")) {
      this.$scope.mode = this.currentUserService.getUserSetting("taskView");
    } else {
      this.$scope.mode = "list";
    }
  };

  private setInitialSort = () => {
    if (this.currentUserService.getUserSetting("taskOrder")) {
      this.sortTask = this.currentUserService.getUserSetting("taskOrder");

      const sortedByKey = this.sortTask.replace("-", "");
      this.$scope.isSortedBy[sortedByKey] = true;
      this.$scope.isAscSort = this.sortTask.includes("-");
    }
  };

  private processFilterIds = (filter: IHubFilter): string[] => {
    if (filter.employeeIds && filter.teamIds) {
      return filter.employeeIds.concat(filter.teamIds);
    }
    if (!filter.teamIds) {
      return filter.employeeIds;
    }
    if (!filter.employeeIds) {
      return filter.teamIds;
    }
  };

  private loadTasks(ownerId: string | string[]): IPromise<unknown> {
    const getTasksParams: IRestLayerRequest = {
      filter: {
        $and: [
          {
            ...(Array.isArray(ownerId) ? { ownerId: { $in: ownerId } } : { ownerId }),
          },
          {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            $or: [{ sessionId: { $exists: false } }, { sessionId_activeAndFutureSessions: { $in: [] } }],
          },
        ],
      },
      sort: this.sortTask,
      // we want to get all tasks
      limit: -1,
      fields: "id,name,ownerId,dueDate,description,status,sessionId,parentId,parentName,parentType,externalId,externalSystem,archived",
    };

    return this.tasksService.getTasksV2(getTasksParams).then(
      (tasks) => {
        this.setInitialRenderingLimits();

        window.addEventListener("resize", this.onWindowResized);
        this.$scope.$on("$destroy", () => window.removeEventListener("resize", this.onWindowResized));

        let archivedTasksCount = 0;
        const activeTasks = [];

        for (const task of tasks.items) {
          if (task.archived) {
            archivedTasksCount++;
          } else {
            activeTasks.push(task);
          }
        }
        this.$scope.archivedTasksCount = archivedTasksCount;
        this.clearInvalidGoalsParentIds(activeTasks);
        this.clearInvalidMetricsParentIds(activeTasks);
        this.generateTasksForBothViews(activeTasks);

        delete this.$scope.indicators.loadingTasks;
      },
      (err) => (this.$scope.indicators.loadingTasks = { error: err })
    );
  }

  private clearInvalidGoalsParentIds(tasks: Task[]): void {
    const goalIds = tasks.filter((task) => task.parentId && task.parentType === "goal").map((task) => task.parentId);
    if (goalIds.length) {
      const uniqueGoalIds = Array.from(new Set(goalIds));
      const goalsRequestParams: IRestLayerRequest = {
        filter: {
          $and: [{ _id: { $in: uniqueGoalIds } }],
        },
        fields: "id,name",
        limit: -1,
      };
      this.goalService
        .getGoalsV2(goalsRequestParams)
        .then((response) => {
          const goals = response.items.reduce((map, goal) => {
            return map.set(goal.id, goal);
          }, new Map());

          tasks.forEach((task) => {
            if (task.parentId && task.parentType === "goal") {
              task.parentName = goals.get(task.parentId)?.name || "";
              task.parentId = goals.get(task.parentId)?.id || "";
            }
          });
        })
        .catch(() => {
          this.clearParentRelations(tasks);
        });
    }
  }

  private clearInvalidMetricsParentIds(tasks: Task[]): void {
    const metricIds = tasks.filter((task) => task.parentId && task.parentType === "metric").map((task) => task.parentId);
    if (metricIds.length) {
      const uniqueMetricIds = Array.from(new Set(metricIds));
      const metricsRequestParams: IRestLayerRequest = {
        filter: {
          $and: [{ _id: { $in: uniqueMetricIds } }],
        },
        fields: "id,name",
        limit: -1,
      };
      this.metricService
        .getMetricsV2(metricsRequestParams)
        .then((response) => {
          const metrics = response.items.reduce((map, metric) => {
            return map.set(metric.id, metric);
          }, new Map());

          tasks.forEach((task) => {
            if (task.parentId && task.parentType === "metric") {
              task.parentName = metrics.get(task.parentId)?.name || "";
              task.parentId = metrics.get(task.parentId)?.id || "";
            }
          });
        })
        .catch(() => {
          this.clearParentRelations(tasks);
        });
    }
  }

  private clearParentRelations(tasks: Task[]) {
    tasks.forEach((task) => {
      task.parentName = "";
      task.parentId = "";
    });
  }
  private generateTasksForBothViews = (tasks: Task[]): void => {
    this.$scope.taskList = tasks;
    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };

    for (const task of tasks) {
      this.$scope.tasksColumns[task.status]?.push(task);
    }
  };

  private onTaskCreated = (args: ITaskCreatedArgs): void => {
    const newTaskOwner = args.task.ownerId;
    if (this.tasksOwner.indexOf(newTaskOwner) === -1) {
      return;
    }

    this.increaseAllRenderingLimitsBy(1);

    if (args.task.parentId) {
      this.loadTaskParent(args.task.parentType, args.task.parentId).then((parent) => {
        args.task.parentName = parent.name;
        this.addTaskIntoArrays(args.task);
      });
    } else {
      this.addTaskIntoArrays(args.task);
    }

    this.$scope.isCreatingNewKanbanTask = false;
  };

  private addTaskIntoArrays = (task: Task): void => {
    const taskStatus = task.status ? task.status : "todo";

    this.$scope.tasksColumns[taskStatus]?.unshift(task);
    this.$scope.taskList.push(task);
  };

  private loadTaskParent = (parentType: TaskParentType, parentId: string): IPromise<IGoal | Metric> => {
    switch (parentType) {
      case "goal":
        return this.goalService.getGoalWithFields(parentId, "name").then((goal) => goal);
      case "metric":
        return this.metricService.getMetric(parentId).then((metric) => metric);
    }
  };

  private onTaskDeleted = (args: ITaskDeletedArgs): void => {
    this.removeTaskFromViews(args.task);
  };

  private deleteTask = (task: Task): void => {
    this.confirmDeleteService.confirmDeleteItem({ itemKey: "task", itemToCase: true }).subscribe(() => {
      this.tasksService.delete(task.id).then(() => {
        this.removeTaskFromViews(task);
      });
    });
  };

  private removeTaskFromViews = (task: Task): void => {
    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === task.id) {
        this.$scope.taskList.splice(i, 1);
      }
    }

    for (let i = 0; i < this.$scope.tasksColumns[task.status].length; i++) {
      if (this.$scope.tasksColumns[task.status][i].id === task.id) {
        this.$scope.tasksColumns[task.status].splice(i, 1);
      }
    }
  };

  private openTaskFormInKanban = (): void => {
    if (this.$scope.isCreatingNewKanbanTask) {
      this.$scope.$broadcast("focusTextInput");
    } else {
      this.$scope.isCreatingNewKanbanTask = true;
    }
  };

  private closeFormTaskInKanban = (): void => {
    this.$scope.isCreatingNewKanbanTask = false;
  };

  private createNewTaskInList = (): void => {
    const newTask = {
      ownerId: getCurrentUserId(),
      name: localize("new_task"),
      description: "",
      status: "todo",
    };

    this.tasksService.create(newTask).then(
      (data) => {
        this.increaseAllRenderingLimitsBy(1);
        data.isNew = true;
        data.name = "";
        this.$scope.taskList.unshift(data);
        this.$scope.tasksColumns.todo.unshift(data);
        this.$timeout(() => document.querySelector<HTMLElement>(`#task${data.id} [ng-model='task.name']`)?.focus(), 200);
      },
      () => {
        // do something
      }
    );
  };

  private onTaskUpdated = (args: ITaskUpdatedArgs) => {
    // if broadcasted task i archived -> remove it from the view if present
    if (args.task.archived) {
      this.removeTaskFromViews(args.task);
      this.$scope.archivedTasksCount++;
      return;
    } else if (Object.keys(args.update).includes("archived")) {
      this.$scope.archivedTasksCount--;
    }

    if (args.task.parentId) {
      this.loadTaskParent(args.task.parentType, args.task.parentId)
        .then((parent) => {
          args.task.parentName = parent.name;
        })
        .catch(() => {
          args.task.parentId = "";
          args.task.parentName = "";
        })
        .finally(() => {
          this.processTaskAfterUpdate(args);
        });
    } else {
      this.processTaskAfterUpdate(args);
    }
  };

  private processTaskAfterUpdate = (args: ITaskUpdatedArgs): void => {
    let taskFound = true;
    if (Object.keys(args.update).includes("archived")) {
      taskFound = false;
    }

    const indexTaskColumns = this.$scope.tasksColumns[args.task.status]?.findIndex((task: Task) => task.id === args.task.id);
    const indexTaskList = this.$scope.taskList.findIndex((task: Task) => task.id === args.task.id);

    if (indexTaskColumns !== undefined && indexTaskColumns !== -1) {
      taskFound = true;
      this.$scope.tasksColumns[args.task.status][indexTaskColumns] = args.task;
    }

    if (indexTaskList !== -1) {
      taskFound = true;
      this.$scope.taskList[indexTaskList] = args.task;
    }

    // if task is not present -> add it to collections
    if (!taskFound) {
      this.onTaskCreated({ task: args.task, sessionId: args.sessionId });
    }

    // if status is changed -> move task to the column it belongs and sort the list
    if (Object.keys(args.update).includes("status")) {
      this.onTaskStatusChanged(args.task);
    }
  };

  private dropped = (event, index, type, external, column: string): void => {
    for (let i = 0; i < this.$scope.tasksColumns[column].length; i++) {
      if (this.$scope.tasksColumns[column][i].status !== column) {
        this.$scope.tasksColumns[column][i].status = column;

        this.updateTask(this.$scope.tasksColumns[column][i], "status");
        this.updateListTask(this.$scope.tasksColumns[column][i]);

        // animation for done
        if (column === "done") {
          this.$timeout(() => {
            document.querySelector(`[id="${this.$scope.tasksColumns[column][i].id}"] .task`).classList.add("celebration");
            this.$timeout(() => {
              document.querySelector(`[id="${this.$scope.tasksColumns[column][i].id}"] .task`).classList.remove("celebration");
            }, 900);
          }, 120);
        }
      }
    }
  };

  private setDueDate = (task: Task): void => {
    this.updateTask(task, "dueDate");
  };

  private updateListTask = (task) => {
    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === task.id) {
        this.$scope.taskList[i] = task;
      }
    }
  };

  private updateTask = (task, field) => {
    const update = { [field]: task[field] };
    this.tasksService.update(task.id, update);
  };

  private onTaskStatusChanged = (changedTask: Task) => {
    if (this.sortTask && this.sortTask.includes("status")) {
      this.sortBy(this.sortTask, { changeAscDesSort: false });
      return;
    }

    for (const task of this.$scope.taskList) {
      if (task.id === changedTask.id) {
        task.status = changedTask.status;
      }
    }

    this.$scope.tasksColumns = {
      done: [],
      inprogress: [],
      todo: [],
    };

    for (const task of this.$scope.taskList) {
      this.$scope.tasksColumns[task.status]?.push(task);
    }
  };

  private archive = (task: Task) => {
    this.removeTaskFromViews(task);
    task.archived = true;
    this.updateTask(task, "archived");
    this.$scope.archivedTasksCount++;
  };

  private clone = (task: Task): void => {
    const clonedTask = angular.copy(task);

    this.tasksService.create(clonedTask, { gtmhubAdditionalParams: { cloned: true } }).then(
      (newTask) => {
        this.increaseAllRenderingLimitsBy(1);
        newTask.isNew = true;
        this.$scope.taskList.push(newTask);
        this.$scope.tasksColumns[newTask.status]?.unshift(newTask);

        this.$timeout(() => document.querySelector<HTMLElement>(`#task${newTask.id} [ng-model='task.name']`)?.focus(), 200);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private setInitialRenderingLimits = (): void => {
    const { listLimitStep, kanbanLimitStep } = this.calculateRenderingSteps();
    this.$scope.renderingOptions = {
      list: { limitTo: listLimitStep, step: listLimitStep },
      kanban: {
        todo: { limitTo: kanbanLimitStep },
        inprogress: { limitTo: kanbanLimitStep },
        done: { limitTo: kanbanLimitStep },
        step: kanbanLimitStep,
      },
    };
  };

  private calculateRenderingSteps = (): { listLimitStep: number; kanbanLimitStep: number } => {
    const windowHeight = document.documentElement.clientHeight;
    const enlargementCoefficient = 1.1;

    return {
      listLimitStep: Math.ceil((windowHeight * enlargementCoefficient) / sizes.taskRowHeight),
      kanbanLimitStep: Math.ceil((windowHeight * enlargementCoefficient) / sizes.taskKanbanHeight),
    };
  };

  private increaseListLimit = (): void => {
    if (this.$scope.renderingOptions.list.limitTo < this.$scope.taskList.length) {
      this.$scope.renderingOptions.list.limitTo += this.$scope.renderingOptions.list.step;
    }
  };

  private increaseKanbanLimit = (columnName: GtmhubTaskStatus): void => {
    if (this.$scope.renderingOptions.kanban[columnName].limitTo < this.$scope.tasksColumns[columnName].length) {
      this.$scope.renderingOptions.kanban[columnName].limitTo += this.$scope.renderingOptions.kanban.step;
    }
  };

  private increaseAllRenderingLimitsBy = (by: number): void => {
    this.$scope.renderingOptions.list.limitTo += by;
    this.$scope.renderingOptions.kanban.todo.limitTo += by;
    this.$scope.renderingOptions.kanban.inprogress.limitTo += by;
    this.$scope.renderingOptions.kanban.done.limitTo += by;
  };

  private onWindowResized = (): void => {
    const { listLimitStep, kanbanLimitStep } = this.calculateRenderingSteps();
    if (this.$scope.renderingOptions.list.step < listLimitStep) {
      this.$scope.renderingOptions.list.step = listLimitStep;
    }
    if (this.$scope.renderingOptions.kanban.step < kanbanLimitStep) {
      this.$scope.renderingOptions.kanban.step = kanbanLimitStep;
    }
  };

  private openFilterModal = (): void => {
    this.taskFilterModalService.open();
  };
}
